import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { ItemFilter } from '../models/menu.model';

export interface ActivableFilter extends ItemFilter {
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FiltersService {
  private apiService = inject(ApiService);
  private dataService = inject(DataService);
  private translateService = inject(TranslateService);

  private lang: string = '';

  hasFilters = signal<boolean>(false);
  allFilters = signal<ActivableFilter[]>([]);
  loading = signal<boolean>(false);

  readonly activeFilters = computed(() => this.allFilters().filter(filter => filter.isActive));
  readonly hasActiveFilters = computed(() => this.activeFilters().length > 0);

  constructor() {
    // Prefetch as soon as we know the restaurant has filters, so the bottom
    // sheet already has data by the time the user taps the filter icon.
    effect(() => {
      if (this.hasFilters() && this.dataService.restaurant()) {
        this.setFilters();
      }
    });
  }

  setFilters(): void {
    if (this.allFilters().length > 0 && this.lang === this.translateService.currentLang) {
      return;
    }
    this.loading.set(true);
    this.apiService.getItemFilters(this.dataService.restaurant().id)
      .subscribe({
        next: response => {
          const activeIds = new Set(this.activeFilters().map(filter => filter.id));
          this.allFilters.set(response.map(filter => ({ ...this.makeActivableFilter(filter), isActive: activeIds.has(filter.id) })));
        },
        complete: () => this.loading.set(false),
        error: () => this.loading.set(false),
      });
    this.lang = this.translateService.currentLang;
  }

  updateFilters(filters: ActivableFilter[]): void {
    this.allFilters.set(filters);
  }

  clearFilters(): void {
    this.allFilters.update(filters => filters.map(filter => ({ ...filter, isActive: false })));
  }

  private makeActivableFilter(filter: ItemFilter): ActivableFilter {
    return { ...filter, isActive: false };
  }
}
